import { kioskApi } from '../axios.js';
export async function getMBTIQuestions(shopId, opts) {
    const params = {};
    if (opts?.themeId)
        params.themeId = opts.themeId;
    if (opts?.themeName)
        params.themeName = opts.themeName;
    const { data } = await kioskApi.get(`/mbti/shops/${shopId}/questions`, { params });
    return data;
}
export async function generateMBTIQuestions(shopId, perSpectrum = 50, theme) {
    console.warn('generateMBTIQuestions is deprecated; use generateMBTIShopQuestionsAsync or generateMBTIThemeQuestionsAsync');
    const { data } = await kioskApi.post(`/mbti/shops/${shopId}/generate-questions`, { perSpectrum, theme, useLLM: true });
    return data;
}
export async function getMBTIMappings(shopId) {
    const { data } = await kioskApi.get(`/mbti/shops/${shopId}/mappings`);
    return data;
}
export async function generateMBTIMappings(shopId) {
    console.warn('generateMBTIMappings is deprecated; use generateMBTIMappingsAsync');
    const { data } = await kioskApi.post(`/mbti/shops/${shopId}/generate-mappings`, { useLLM: true });
    return data;
}
// Enqueue async mappings generation for a shop and return { jobId }
export async function generateMBTIMappingsAsync(shopId) {
    const { data } = await kioskApi.post(`/mbti/shops/${shopId}/generate-mappings-async`, { useLLM: true });
    return data;
}
// Generate MBTI bar explanations for this shop (non-persistent for now)
export async function generateMBTIBarExplanations(shopId) {
    console.warn('generateMBTIBarExplanations is deprecated; it will enqueue an async job and return the final payload once ready');
    // Enqueue the (deprecated) shop-scoped generation job which returns a { jobId }
    const { data } = await kioskApi.post(`/mbti/shops/${shopId}/generate-bar-explanations`, {});
    const jobId = data?.jobId;
    if (!jobId)
        throw new Error('Failed to enqueue bar explanations job');
    // Poll the job status until completion or timeout
    const timeoutMs = Number(process.env.MBTI_JOB_POLL_TIMEOUT_MS || 300000);
    const intervalMs = 2000;
    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (Date.now() - start > timeoutMs)
            throw new Error('Bar explanations job timed out');
        try {
            const job = await getMBTIJob(jobId);
            if (!job)
                throw new Error('Job not found');
            if (job.status === 'succeeded') {
                return job.result;
            }
            if (job.status === 'failed') {
                throw new Error(job.error || 'Bar explanations job failed');
            }
        }
        catch (e) {
            // ignore transient errors and continue polling
        }
        // wait
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, intervalMs));
    }
}
// Enqueue async bar explanations generation for a theme and return { jobId }
export async function generateMBTIBarExplanationsAsync(themeId, shopId) {
    const body = { useLLM: true };
    if (shopId)
        body.shopId = shopId;
    const { data } = await kioskApi.post(`/mbti/themes/${themeId}/generate-bar-explanations-async`, body);
    return data;
}
// Fetch persisted MBTI bar explanations for this shop
export async function getMBTIBarExplanations(shopId) {
    const { data } = await kioskApi.get(`/mbti/shops/${shopId}/bar-explanations`);
    return data;
}
export async function submitMBTIResponse(instanceId, payload) {
    const { data } = await kioskApi.post(`/mbti/kiosk-instances/${instanceId}/responses`, payload);
    return data;
}
export async function submitMBTIResult(instanceId, payload) {
    const { data } = await kioskApi.post(`/mbti/kiosk-instances/${instanceId}/result`, payload);
    return data;
}
// =====================
// Theme management APIs
// =====================
// List all MBTI themes
export async function listMBTIThemes() {
    const { data } = await kioskApi.get('/mbti/themes');
    return data;
}
// Create a new MBTI theme
export async function createMBTITheme(name) {
    const { data } = await kioskApi.post('/mbti/themes', { name });
    return data;
}
// Get the current theme selection for a shop
export async function getShopMBTITheme(shopId) {
    const { data } = await kioskApi.get(`/mbti/shops/${shopId}/theme`);
    // Shape expected by callers: { theme_id: string }
    return data;
}
// Set the current theme selection for a shop
export async function setShopMBTITheme(shopId, themeId) {
    const { data } = await kioskApi.put(`/mbti/shops/${shopId}/theme`, { themeId });
    return data;
}
// Get questions metadata for a theme
export async function getMBTIThemeQuestions(themeId) {
    const { data } = await kioskApi.get(`/mbti/themes/${themeId}/questions`);
    return data;
}
// Generate question bank for a theme
export async function generateMBTIThemeQuestions(themeId, perSpectrum = 50) {
    const { data } = await kioskApi.post(`/mbti/themes/${themeId}/generate-questions`, { perSpectrum, useLLM: true });
    return data;
}
// Enqueue async generation for a theme and return { jobId }
export async function generateMBTIThemeQuestionsAsync(themeId, perSpectrum = 50) {
    const { data } = await kioskApi.post(`/mbti/themes/${themeId}/generate-questions-async`, { perSpectrum, useLLM: true });
    return data;
}
// Enqueue async generation for a shop and return { jobId }
export async function generateMBTIShopQuestionsAsync(shopId, perSpectrum = 50, theme) {
    const { data } = await kioskApi.post(`/mbti/shops/${shopId}/generate-questions-async`, { perSpectrum, theme, useLLM: true });
    return data;
}
// Get job status/result
export async function getMBTIJob(jobId) {
    const { data } = await kioskApi.get(`/mbti/jobs/${jobId}`);
    return data;
}
// =====================
// Travel3 Proxy APIs
// =====================
/**
 * Sleep function for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
/**
 * Make API call with retry logic for transient failures
 */
const makeApiCallWithRetry = async (apiCall, maxRetries = 2) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            if (attempt > 0) {
                const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
                console.log(`[MBTI API] Retry attempt ${attempt}/${maxRetries} after ${delay}ms delay`);
                await sleep(delay);
            }
            return await apiCall();
        }
        catch (error) {
            lastError = error;
            // Don't retry on client errors (4xx) or specific server errors that won't recover
            if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
                throw error;
            }
            // Don't retry on the final attempt
            if (attempt === maxRetries) {
                throw error;
            }
            console.log(`[MBTI API] Attempt ${attempt + 1} failed, retrying... Error:`, error.message);
        }
    }
    throw lastError;
};
/**
 * Generate an image via the Travel3 proxy
 * @param body The request body for the image generation, e.g., { prompt, ... }
 */
export async function generateTravel3Image(body) {
    return makeApiCallWithRetry(async () => {
        const { data } = await kioskApi.post('/mbti/travel3/images/generate', body);
        return data;
    });
}
/**
 * Generate a video via the Travel3 proxy
 * @param body The request body for the video generation, e.g., { imageUrl, ... }
 */
export async function generateTravel3Video(body) {
    return makeApiCallWithRetry(async () => {
        const { data } = await kioskApi.post('/mbti/travel3/videos/generate', body);
        return data;
    });
}
/**
 * Upload generated image to photowall
 * @param imageUrl The generated image URL from Travel3
 * @param metadata The metadata including mbtiType, shopId, instanceId, etc.
 */
export async function uploadToPhotowall(imageUrl, metadata) {
    const { data } = await kioskApi.post('/mbti/media/upload-to-photowall', {
        imageUrl,
        metadata
    });
    return data;
}
/**
 * Check the Travel3 quota via the proxy
 */
export async function checkTravel3Quota() {
    const { data } = await kioskApi.get('/mbti/travel3/quota');
    return data;
}
/**
 * Uploads a base64 encoded image to the server
 * @param image The base64 encoded image string
 */
/**
 * Uploads a base64 encoded selfie to the server, which then saves it to S3.
 * @param image The base64 encoded image string
 * @param userId A unique identifier for the user
 */
export async function uploadBase64Selfie(image, userId) {
    const { data } = await kioskApi.post('/selfie/upload', { image, userId });
    return data;
}
